<template>
  <div class="aroma-pay-result">
    <aroma-secondary-nav title="支付结果" fallback="AromaShop" />
    <div v-if="loading" class="aroma-page-state">
      <van-loading size="28px">正在确认支付结果...</van-loading>
    </div>
    <main v-else class="aroma-pay-result__content">
      <img
        class="aroma-pay-result__icon"
        :src="resultIcon"
        :alt="resultState.title"
      />
      <h1>{{ resultState.title }}</h1>
      <p>{{ resultDescription }}</p>

      <section v-if="order" class="aroma-pay-result__order">
        <div>
          <span>商品名称</span>
          <strong>{{ orderProductName }}</strong>
        </div>
        <div>
          <span>支付金额</span>
          <strong class="is-price">￥{{ orderPrice }}</strong>
        </div>
      </section>

      <van-button
        v-if="resultState.canPay"
        class="aroma-pay-result__primary"
        round
        block
        type="primary"
        :loading="paying"
        @click="continuePayment"
      >继续支付</van-button>
      <van-button
        v-if="resultState.key === 'checking'"
        class="aroma-pay-result__primary"
        round
        block
        type="primary"
        @click="loadOrder"
      >刷新结果</van-button>
      <van-button class="aroma-pay-result__secondary" round block @click="backToShop">返回商城</van-button>
    </main>
  </div>
</template>

<script>
import API from '@/api'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import { buildPayResultUrl, formatPrice, getAromaProductConfig, getOrderResultState } from '@/utils/aroma-shop'
import {
  WECHAT_APP_ID,
  buildWechatAuthorizeUrl,
  clearPendingOrder,
  getMatchingPendingOrder,
  getPendingOrder,
  isWeixinBrowser,
  savePendingOrder,
  startAlipay,
  startWechatPay
} from '@/utils/aroma-pay'
import { getSniStorageScope } from '@/utils/sni-session'

export default {
  name: 'AromaPayResult',

  components: {
    AromaSecondaryNav
  },

  data () {
    return {
      loading: false,
      paying: false,
      order: null,
      queryFailed: false,
      pendingOrder: null
    }
  },

  computed: {
    sn () {
      return this.$route.query.sn || ''
    },

    storageScope () {
      return getSniStorageScope(this.sn)
    },

    productConfig () {
      return getAromaProductConfig(this.$route.query.productId)
    },

    orderId () {
      return this.$route.query.orderId || this.pendingOrder?.orderId || ''
    },

    matchingPendingOrder () {
      return getMatchingPendingOrder(this.pendingOrder, this.orderId)
    },

    resultState () {
      return getOrderResultState(this.queryFailed ? undefined : this.order?.orderStatus)
    },

    resultDescription () {
      if (this.resultState.key === 'success') return '您的商品备货中，即将快马加鞭而来～'
      if (this.resultState.key === 'pending') return '订单超过30分钟将自动取消'
      if (this.resultState.key === 'closed') return '支付时间已超时，请重新下单'
      return '暂未确认最终结果，请稍后刷新'
    },

    resultIcon () {
      return this.resultState.key === 'success'
        ? require('@/assets/img/success.png')
        : require('@/assets/img/error.png')
    },

    orderPrice () {
      return formatPrice(this.order?.payPrice)
    },

    orderProductName () {
      return this.order?.packageName || this.order?.orderPackageName || this.productConfig.packageName
    }
  },

  created () {
    this.pendingOrder = getPendingOrder(this.storageScope)
  },

  async mounted () {
    await this.loadOrder()
    window.history.replaceState(null, '', window.location.href)
    window.addEventListener('popstate', this.backToShop)
    if (this.$route.query.resumePay === '1' && this.$route.query.code && this.resultState.canPay) {
      await this.continuePayment()
    }
  },

  beforeDestroy () {
    window.removeEventListener('popstate', this.backToShop)
  },

  methods: {
    async loadOrder () {
      if (!this.orderId) {
        this.queryFailed = true
        return
      }
      this.loading = true
      this.queryFailed = false
      try {
        const response = await API.aromaOrderDetail(this.orderId)
        if (response?.code !== '1' || !response.data) throw new Error(response?.msg || '订单查询失败')
        this.order = response.data
        if (
          (Number(this.order.orderStatus) === 2 || Number(this.order.orderStatus) === 3) &&
          this.matchingPendingOrder
        ) {
          clearPendingOrder(this.storageScope)
          this.pendingOrder = null
        }
      } catch (error) {
        this.queryFailed = true
      } finally {
        this.loading = false
      }
    },

    async continuePayment () {
      this.paying = true
      try {
        if (this.matchingPendingOrder?.payData) {
          await this.startPendingPayment(this.matchingPendingOrder)
          return
        }

        const cashierResponse = await API.aromaCashiers()
        const isWeixin = isWeixinBrowser(window.navigator.userAgent)
        const supportedThirdParty = isWeixin ? 2 : 1
        const cashier = (cashierResponse?.data || []).find(item => Number(item.thirdParty) === supportedThirdParty)
        if (!cashier) throw new Error('当前环境暂无可用支付方式')

        if (isWeixin && !this.$route.query.code) {
          const result = this.$router.resolve({
            name: 'AromaPayResult',
            query: {
              ...(this.sn ? { sn: this.sn } : {}),
              orderId: this.orderId,
              productId: this.productConfig.id,
              resumePay: '1'
            }
          })
          window.location.assign(buildWechatAuthorizeUrl({
            appId: WECHAT_APP_ID,
            redirectUri: new URL(result.href, window.location.origin).toString(),
            state: 'aroma-pay-result'
          }))
          return
        }

        const response = await API.aromaPayOrder({
          oid: this.orderId,
          jsCode: this.$route.query.code || '',
          cashierTypeId: cashier.id,
          returnUrl: buildPayResultUrl(window.location.origin, this.sn, this.orderId, this.productConfig.id)
        })
        if (response?.code !== '1' || !response.data) throw new Error(response?.msg || '支付发起失败')
        const pending = {
          orderId: this.orderId,
          payType: supportedThirdParty === 2 ? 'wechat' : 'alipay',
          payData: response.data
        }
        this.pendingOrder = pending
        savePendingOrder(this.storageScope, pending)
        await this.startPendingPayment(pending)
      } catch (error) {
        this.$toast(error?.message || error?.err_msg || '支付失败')
      } finally {
        this.paying = false
      }
    },

    async startPendingPayment (pending) {
      if (pending.payType === 'wechat') {
        await startWechatPay(pending.payData)
        await this.loadOrder()
        return
      }
      startAlipay(pending.payData)
    },

    backToShop () {
      this.$router.replace({ name: 'AromaShop', query: this.sn ? { sn: this.sn } : {} })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/aroma-commerce.scss';

.aroma-pay-result {
  min-height: 100%;
  padding: 1px 24px 40px;
  background: #fff;
  box-sizing: border-box;

  &__content {
    max-width: 420px;
    margin: 0 auto;
    text-align: center;
  }

  &__icon {
    display: block;
    width: 112px;
    height: 112px;
    margin: 90px auto 0;
    object-fit: contain;
  }

  h1 {
    margin-top: 26px;
    color: #1f2329;
    font-size: 24px;
    font-weight: 500;
    line-height: 34px;
  }

  p {
    margin: 10px auto 0;
    color: #8a8f98;
    font-size: 15px;
    line-height: 23px;
  }

  &__order {
    margin: 34px 0 28px;
    padding: 4px 16px;
    background: #f7f7f7;
    border-radius: 6px;

    div {
      display: flex;
      min-height: 48px;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      border-bottom: 1px solid #e8e8e8;

      &:last-child {
        border-bottom: 0;
      }
    }

    span,
    strong {
      font-size: 15px;
      line-height: 22px;
    }

    span {
      color: #646a73;
    }

    strong {
      color: #1f2329;
      font-weight: 400;
    }

    .is-price {
      color: #ff4c45;
      font-weight: 600;
    }
  }

  &__primary,
  &__secondary {
    height: 46px;
    margin-top: 14px;
    font-size: 16px;
  }

  &__primary {
    background: #596afd;
    border-color: #596afd;
  }

  &__secondary {
    color: #596afd;
    border: 1px solid #596afd;
  }
}
</style>
